export type DateDisplayPreferences = { locale?: string; timeZone?: string };

const formatters = new Map<string, Intl.DateTimeFormat>();
const calendarDate = /^\d{4}-\d{2}-\d{2}$/;

export function dateDisplayPreferences(value?: unknown): DateDisplayPreferences {
    const defaults = Intl.DateTimeFormat().resolvedOptions();
    const source = value && typeof value === 'object' ? value as DateDisplayPreferences : {};
    let locale = defaults.locale;
    let timeZone = defaults.timeZone;
    try {
        if (typeof source.locale === 'string' && source.locale.length <= 100)
            locale = Intl.getCanonicalLocales(source.locale)[0] || locale;
    } catch { /* Use the device locale when a preference is invalid. */ }
    try {
        if (typeof source.timeZone === 'string' && source.timeZone.length <= 100)
            timeZone = new Intl.DateTimeFormat(undefined, { timeZone: source.timeZone }).resolvedOptions().timeZone;
    } catch { /* Use the device time zone when a preference is invalid. */ }
    return { locale, timeZone };
}

function parseDate(value: unknown): Date | null {
    try {
        let date: Date;
        if (value instanceof Date)
            date = value;
        else if (typeof value === 'string' && value.trim()) {
            const text = value.trim();
            if (/^\d{4}(?:-\d{2})?$/.test(text))
                return null;
            date = new Date(text);
            const day = /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(text) ? text.slice(0, 10) : null;
            if (day && new Date(day).toISOString().slice(0, 10) !== day)
                return null;
        } else if (typeof value === 'number')
            date = new Date(value);
        else if (value && typeof value === 'object') {
            const timestamp = value as { toDate?: () => Date; seconds?: number; nanoseconds?: number; timestampValue?: string };
            if (typeof timestamp.toDate === 'function')
                date = timestamp.toDate();
            else if (typeof timestamp.seconds === 'number')
                date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1e6);
            else if (typeof timestamp.timestampValue === 'string')
                return parseDate(timestamp.timestampValue);
            else
                return null;
        } else
            return null;
        return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
    } catch {
        return null;
    }
}

function format(value: unknown, fallback: string, preferences: DateDisplayPreferences, includeTime: boolean): string {
    const date = parseDate(value);
    if (!date)
        return fallback;
    const dateOnly = typeof value === 'string' && calendarDate.test(value.trim());
    const timeZone = dateOnly ? 'UTC' : preferences.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric',
        ...(includeTime && !dateOnly ? { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' } : {}),
        timeZone };
    const key = JSON.stringify([preferences.locale, options]);
    let formatter = formatters.get(key);
    if (!formatter) {
        try { formatter = new Intl.DateTimeFormat(preferences.locale, options); }
        catch { formatter = new Intl.DateTimeFormat(undefined, { ...options, timeZone: dateOnly ? 'UTC' : undefined }); }
        if (formatters.size >= 64)
            formatters.clear();
        formatters.set(key, formatter);
    }
    return formatter.format(date);
}

export const formatLocalDate = (value: unknown, fallback = '', preferences: DateDisplayPreferences = {}) =>
    format(value, fallback, preferences, false);

export const formatLocalDateTime = (value: unknown, fallback = '', preferences: DateDisplayPreferences = {}) =>
    format(value, fallback, preferences, true);

export function formatLocalDateValue(value: unknown): string | null {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(value.trim()) ||
        value instanceof Date || value && typeof value === 'object' && ('toDate' in value || 'seconds' in value || 'timestampValue' in value))
        return formatLocalDateTime(value) || null;
    return null;
}

export function localDateInputValue(date = new Date()): string {
    return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
